// ═══════════════════════════════════════════════════════════════════════
// Temps › Clôture et paie (RH) — conception § 11, § 12, § 14.4. Par site et par mois :
//   • la liste de contrôle d'avant clôture, un lien par ligne (« Voir », « Traiter »…) ;
//   • clôturer (confirmation) ; rouvrir (DRH, motif obligatoire, autre personne que le clôtureur) ;
//   • les versions de l'export vers la paie : télécharger (CSV ou JSON, avec l'empreinte),
//     rééditer (motif), marquer intégré en paie.
// Les droits et les règles sont ceux du serveur (services de l'agent M).
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, LockOpen, ArrowRight, FileSpreadsheet, FileJson, RotateCcw, FileCheck2, AlertTriangle, Info, CheckCircle2, XCircle, Calculator, FilePlus2 } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, Badge, BandeauInfo, EtatVide, Squelette, Selecteur } from '../../design/composants';
import { Fenetre, Champ, PiedFenetre } from './reglagesCommun';
import { NavTemps, useDroitsTemps, AccesTemps, lireRh, ecrireRh, messageErreur, moisLong, dateHeure, moisPrecedentIso, aujourdhui, STATUTS_PERIODE, STATUTS_EXPORT } from './rhCommun';

function LigneControle({ l, ton, action = null }) {
  const [ouvert, setOuvert] = useState(false);
  const noms = (l.elements || []).map((e) => [e.employe_nom, e.date_jour || e.date_debut || e.semaine_debut].filter(Boolean).join(' · ')).filter(Boolean);
  const Icone = ton === 'danger' ? XCircle : AlertTriangle;
  return (
    <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 300px', minWidth: 0 }}>
        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'flex-start' }}>
          <Icone size={15} aria-hidden="true" style={{ color: ton === 'danger' ? 'var(--sg-danger)' : 'var(--sg-alerte)', flexShrink: 0, marginTop: 2 }} />
          <span>{l.libelle}{l.nombre ? <strong style={{ fontWeight: 500 }}> ({l.nombre})</strong> : null}</span>
        </span>
        {noms.length > 0 && (
          <div style={{ marginLeft: 23 }}>
            <button type="button" className="rh-btn rh-btn-lien rh-btn-petit" onClick={() => setOuvert(!ouvert)}>{ouvert ? 'Masquer le détail' : 'Voir le détail'}</button>
            {ouvert && <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--sg-texte-2)' }}>{noms.slice(0, 20).map((n, i) => <li key={i}>{n}</li>)}{noms.length > 20 && <li>… et {noms.length - 20} autres</li>}</ul>}
          </div>
        )}
      </div>
      {action}
      {l.lien?.vers ? <Link to={l.lien.vers} className="rh-btn rh-btn-petit rh-btn-discret" style={{ textDecoration: 'none' }}>{l.lien.libelle}<ArrowRight size={14} aria-hidden="true" /></Link>
        : l.lien?.libelle ? <span className="rh-form-aide">{l.lien.libelle}</span> : null}
    </li>
  );
}

// Rubriques proposées pour une régularisation manuelle (codes du catalogue de paie ; le serveur
// refuse tout code inconnu). Heures saisies en minutes, jours et nombres tels quels.
const RUBRIQUES_REGUL = [
  ['HS_TRANCHE_1', 'Heures sup. 1re tranche', 'minutes'], ['HS_TRANCHE_2', 'Heures sup. 2e tranche', 'minutes'],
  ['HEURES_NUIT', 'Heures de nuit', 'minutes'], ['HEURES_DIMANCHE', 'Heures du dimanche', 'minutes'], ['HEURES_FERIE', 'Heures de jour férié', 'minutes'],
  ['ABSENCE_INJUSTIFIEE', 'Absence non justifiée', 'minutes'], ['RETARD', 'Retard', 'minutes'], ['PANIER', 'Paniers', 'nombre'],
  ['CONGE_PAYE', 'Congé payé', 'jours'], ['ABSENCE_MALADIE', 'Absence déclarée (arrêt)', 'jours'],
];

/** Régularisation manuelle d'une journée d'une période clôturée (HS découvertes après la clôture…). */
function FenetreRegularisation({ site, mois, onFermer, onFait }) {
  const [f, setF] = useState({ recherche: '', employe_id: '', date: `${mois}-01`, code: 'HS_TRANCHE_1', quantite: '', motif: '' });
  const [salaries, setSalaries] = useState([]);
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => {
    let annule = false;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get('/api/employes', { params: { site_id: site.site_id, recherche: f.recherche || undefined, limite: 20, statut: 'Actif' } });
        if (!annule) setSalaries(r.data.employes || []);
      } catch (e) { if (!annule) setSalaries([]); }
    }, 250);
    return () => { annule = true; clearTimeout(t); };
  }, [f.recherche, site.site_id]);
  const rub = RUBRIQUES_REGUL.find((r) => r[0] === f.code);
  const envoyer = async (e) => {
    e.preventDefault(); setErreur('');
    if (!f.employe_id) return setErreur('Choisissez le salarié.');
    if (!f.quantite || Number(f.quantite) === 0) return setErreur('Indiquez une quantité (positive pour un rappel, négative pour une reprise).');
    if (f.motif.trim().length < 3) return setErreur('Le motif est obligatoire.');
    setEnvoi(true);
    try {
      await axios.post('/api/temps/anomalies/regularisations', { employe_id: f.employe_id, date: f.date, elements: { [f.code]: Number(f.quantite) }, motif: f.motif.trim() });
      onFait('Régularisation enregistrée : elle doit être validée par une autre personne (Temps › À traiter).');
    } catch (err) { setErreur(messageErreur(err)); } finally { setEnvoi(false); }
  };
  return (
    <Fenetre titre={`Régularisation manuelle — ${site.site_nom}, ${moisLong(mois)}`} Icone={FilePlus2} onFermer={onFermer}>
      <form onSubmit={envoyer}>
        <p className="rh-form-aide" style={{ marginTop: 0 }}>Pour un élément découvert après la clôture (heures supplémentaires non validées à temps…). La journée d'origine n'est pas modifiée : l'écart est reporté sur le premier mois ouvert, après validation par une seconde personne.</p>
        <Champ libelle="Rechercher le salarié"><input className="rh-champ-saisie" value={f.recherche} onChange={(e) => setF({ ...f, recherche: e.target.value })} placeholder="Nom ou matricule" /></Champ>
        <Champ libelle="Salarié" requis>
          <select className="rh-champ-saisie" value={f.employe_id} onChange={(e) => setF({ ...f, employe_id: e.target.value })}>
            <option value="">Choisir…</option>
            {salaries.map((x) => <option key={x.id} value={x.id}>{[x.prenom, x.nom].filter(Boolean).join(' ')}{x.matricule ? ` (${x.matricule})` : ''}</option>)}
          </select>
        </Champ>
        <Champ libelle="Journée concernée" requis><input type="date" className="rh-champ-saisie" value={f.date} min={`${mois}-01`} max={`${mois}-31`} onChange={(e) => setF({ ...f, date: e.target.value })} /></Champ>
        <Champ libelle="Élément de paie" requis>
          <select className="rh-champ-saisie" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })}>
            {RUBRIQUES_REGUL.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
          </select>
        </Champ>
        <Champ libelle={`Quantité (${rub ? rub[2] : ''})`} requis aide="Positive pour un rappel, négative pour une reprise."><input type="number" step="any" className="rh-champ-saisie" value={f.quantite} onChange={(e) => setF({ ...f, quantite: e.target.value })} /></Champ>
        <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={3} value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} /></Champ>
        {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        <PiedFenetre>
          <Bouton variante="discret" onClick={onFermer}>Annuler</Bouton>
          <Bouton variante="primaire" type="submit" disabled={envoi} Icone={FilePlus2}>Enregistrer</Bouton>
        </PiedFenetre>
      </form>
    </Fenetre>
  );
}

/** Téléchargement authentifié d'un export (le fichier porte son empreinte). */
async function telecharger(exp, format) {
  const r = await axios.get(`/api/temps/rh/exports/${exp.id}/telecharger`, { params: { format }, responseType: 'blob' });
  const dispo = r.headers['content-disposition'] || '';
  const nom = (dispo.match(/filename="([^"]+)"/) || [])[1] || `export-temps-v${exp.version}.${format}`;
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a'); a.href = url; a.download = nom; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return r.headers['x-empreinte-sha256'] || exp.empreinte;
}

function FenetreConfirmation({ titre, Icone, texte, avertissements, motifRequis, libelleMotif = 'Motif', aideMotif, bouton, variante = 'primaire', onFermer, onValider }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const envoyer = async () => { setEnvoi(true); setErreur(''); try { await onValider(motif.trim()); } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); } };
  return (
    <Fenetre titre={titre} Icone={Icone} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <p style={{ margin: '0 0 10px', fontSize: 13 }}>{texte}</p>
      {avertissements?.length > 0 && (
        <BandeauInfo ton="alerte" titre="À savoir avant de confirmer">
          <ul style={{ margin: 0, paddingLeft: 18 }}>{avertissements.map((a) => <li key={a.code}>{a.libelle}{a.nombre ? ` (${a.nombre})` : ''}</li>)}</ul>
        </BandeauInfo>
      )}
      {motifRequis && (
        <Champ libelle={libelleMotif} requis aide={aideMotif}>
          <textarea className="rh-champ-saisie" rows={3} value={motif} maxLength={2000} onChange={(e) => setMotif(e.target.value)} autoFocus />
        </Champ>
      )}
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante={variante} Icone={Icone} onClick={envoyer} disabled={envoi || (motifRequis && motif.trim().length < 3)}>{bouton}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

export default function RhCloturePage() {
  const droits = useDroitsTemps();
  const [params, setParams] = useSearchParams();
  const mois = params.get('mois') || moisPrecedentIso();
  const site = params.get('site') || '';
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [fenetre, setFenetre] = useState(null);
  const maj = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  const charger = useCallback(async () => {
    setErreur(''); setDonnees(null);
    try { setDonnees(await lireRh('/periodes', { mois, site: site || undefined })); } catch (e) { setErreur(messageErreur(e, 'Chargement impossible.')); }
  }, [mois, site]);
  useEffect(() => { if (droits?.rh?.menu?.cloture) charger(); }, [droits, charger]);
  const termine = (m) => { setFenetre(null); setMessage(m); charger(); };
  const agir = async (fn) => { setErreur(''); setMessage(''); try { await fn(); } catch (e) { setErreur(messageErreur(e)); } };

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps', vers: '/temps' }, { libelle: 'Clôture et paie' }]} titre="Clôture et paie"
          sousTitre="Un mois, site par site. Ce qui reste à régler s'affiche avant le bouton de clôture ; l'export vers la paie est produit à la clôture." />
        <NavTemps actif="cloture" />
        <AccesTemps droits={droits} rubrique="cloture">
          <div className="rh-outils">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>Mois
              <input type="month" className="rh-champ-saisie" value={mois} max={aujourdhui().slice(0, 7)} onChange={(e) => e.target.value && maj('mois', e.target.value)} /></label>
            {droits?.rh?.sites?.length > 1 && (
              <div style={{ minWidth: 220 }}><Selecteur libelle="Site" valeur={site} onChange={(v) => maj('site', v)} options={droits.rh.sites.map((s) => ({ valeur: s.id, libelle: s.nom }))} /></div>
            )}
          </div>
          {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
          {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
          {!donnees && !erreur && <Squelette largeur="60%" />}
          {donnees && !donnees.sites.length && <EtatVide Icone={Lock} titre="Aucun site à clôturer dans votre périmètre" />}
          {donnees?.sites.map((s) => {
            const p = s.periode;
            const [ton, libelle] = STATUTS_PERIODE[p.statut] || ['neutre', p.statut];
            return (
              <Carte key={s.site_id} titre={`${s.site_nom} — ${moisLong(mois)}`} Icone={Lock}
                action={(
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Badge ton={ton} point>{libelle}</Badge>
                    {s.peut_rouvrir && (
                      <Bouton petit variante="discret" Icone={LockOpen} onClick={() => setFenetre({ genre: 'rouvrir', site: s })}>Rouvrir</Bouton>
                    )}
                    {['OUVERTE', 'ROUVERTE'].includes(p.statut) && (
                      <Bouton petit variante="primaire" Icone={Lock} disabled={!s.peut_cloturer}
                        title={!s.peut_cloturer ? (s.bloquants.length ? 'Des points bloquants restent à régler.' : 'Vous ne pouvez pas clôturer cette période.') : undefined}
                        onClick={() => setFenetre({ genre: 'cloturer', site: s })}>Clôturer {moisLong(mois)}</Bouton>
                    )}
                  </span>
                )}>
                {p.statut === 'CLOTUREE' && <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>Clôturée le {dateHeure(p.cloture_le)}{p.cloture_par ? ` par ${p.cloture_par}` : ''}. Les journées sont verrouillées ; une correction passe par une régularisation sur le mois suivant.</p>}
                {p.statut === 'CLOTUREE' && droits?.rh?.menu?.a_traiter && (
                  <div style={{ margin: '0 0 8px' }}>
                    <Bouton petit variante="discret" Icone={FilePlus2} onClick={() => setFenetre({ genre: 'regularisation', site: s })}>Régularisation manuelle…</Bouton>
                  </div>
                )}
                {p.statut === 'ROUVERTE' && (
                  <BandeauInfo ton={p.reouverture_echue ? 'danger' : 'alerte'} titre={p.reouverture_echue ? 'Délai de réouverture dépassé : à reclôturer' : 'Période rouverte'}>
                    Rouverte le {dateHeure(p.rouvert_le)}{p.rouvert_par ? ` par ${p.rouvert_par}` : ''}, jusqu'au {dateHeure(p.reouverture_jusqua)}. Motif : {p.motif_reouverture}.
                    {' '}Une autre personne que celle qui a rouvert doit reclôturer.
                  </BandeauInfo>
                )}
                {['OUVERTE', 'ROUVERTE'].includes(p.statut) && (
                  <>
                    <h3 style={{ fontSize: 13, fontWeight: 600, margin: '4px 0 6px' }}>Avant de clôturer</h3>
                    {!s.bloquants.length ? (
                      <p style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '0 0 8px', fontSize: 13, color: 'var(--sg-succes)' }}><CheckCircle2 size={15} aria-hidden="true" />Aucun point bloquant.</p>
                    ) : <ul className="rh-liste">{s.bloquants.map((l) => <LigneControle key={l.code} l={l} ton="danger"
                      action={l.code === 'JOURNEES_NON_CALCULEES' ? (
                        <Bouton petit variante="discret" Icone={Calculator} onClick={() => agir(async () => {
                          const r = (await axios.post('/api/temps/periodes/rattrapage', { site_id: s.site_id, mois })).data.data || {};
                          setMessage(`${r.deposees ?? r.journees ?? 'Les'} journées sont en cours de calcul. Actualisez la page dans une minute.`);
                        })}>Calculer les journées manquantes</Bouton>
                      ) : null} />)}</ul>}
                  </>
                )}
                {s.avertissements.length > 0 && (
                  <>
                    <h3 style={{ fontSize: 13, fontWeight: 600, margin: '10px 0 6px', display: 'flex', gap: 6, alignItems: 'center' }}><Info size={14} aria-hidden="true" />À savoir (non bloquant)</h3>
                    <ul className="rh-liste">{s.avertissements.map((l) => <LigneControle key={l.code} l={l} ton="alerte" />)}</ul>
                  </>
                )}

                <h3 style={{ fontSize: 13, fontWeight: 600, margin: '14px 0 6px', display: 'flex', gap: 6, alignItems: 'center' }}><FileSpreadsheet size={14} aria-hidden="true" />Export vers la paie</h3>
                {!s.exports.length ? <p className="rh-form-aide" style={{ margin: 0 }}>L'export sera produit automatiquement à la clôture.</p> : (
                  <div className="rh-conteneur-tableau">
                    <table className="rh-tableau">
                      <thead><tr><th>Version</th><th>État</th><th>Produit</th><th>Éléments</th><th>Empreinte</th><th aria-label="Actions" /></tr></thead>
                      <tbody>
                        {s.exports.map((x) => {
                          const [tonX, libX] = STATUTS_EXPORT[x.statut] || ['neutre', x.statut];
                          return (
                            <tr key={x.id}>
                              <td>Version {x.version}</td>
                              <td><Badge ton={tonX}>{libX}</Badge>{x.integre_le && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>le {dateHeure(x.integre_le)}{x.integre_par ? ` par ${x.integre_par}` : ''}</span>}</td>
                              <td>{dateHeure(x.genere_le)}{x.genere_par ? <span className="rh-cellule-secondaire" style={{ display: 'block' }}>par {x.genere_par}</span> : null}</td>
                              <td>{x.nb_elements}</td>
                              <td><code title={x.empreinte} style={{ fontSize: 12 }}>{String(x.empreinte).slice(0, 12)}…</code></td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <Bouton petit variante="discret" Icone={FileSpreadsheet} onClick={() => agir(async () => { const e = await telecharger(x, 'csv'); setMessage(`Export téléchargé (CSV). Empreinte : ${e}`); })}>CSV</Bouton>
                                <Bouton petit variante="discret" Icone={FileJson} onClick={() => agir(async () => { const e = await telecharger(x, 'json'); setMessage(`Export téléchargé (JSON). Empreinte : ${e}`); })}>JSON</Bouton>
                                {x.statut === 'GENERE' && s.peut_integrer && p.statut === 'CLOTUREE' && (
                                  <Bouton petit Icone={FileCheck2} onClick={() => setFenetre({ genre: 'integrer', site: s, exp: x })}>Marquer intégré</Bouton>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {s.peut_reediter && (
                  <div style={{ marginTop: 8 }}>
                    <Bouton petit variante="discret" Icone={RotateCcw} onClick={() => setFenetre({ genre: 'reediter', site: s })}>Rééditer l'export</Bouton>
                  </div>
                )}
              </Carte>
            );
          })}

          {fenetre?.genre === 'regularisation' && (
            <FenetreRegularisation site={fenetre.site} mois={mois} onFermer={() => setFenetre(null)} onFait={termine} />
          )}
          {fenetre?.genre === 'cloturer' && (
            <FenetreConfirmation titre={`Clôturer ${moisLong(mois)} — ${fenetre.site.site_nom}`} Icone={Lock} bouton="Clôturer"
              texte="Les journées du mois seront validées et verrouillées, et l'export vers la paie sera produit. Une correction ultérieure passera par une régularisation sur le mois suivant."
              avertissements={fenetre.site.avertissements} onFermer={() => setFenetre(null)}
              onValider={async () => {
                const r = await ecrireRh('post', `/periodes/${fenetre.site.site_id}/${mois}/cloturer`);
                termine(`${fenetre.site.site_nom} : ${moisLong(mois)} clôturé (${r.journees} journées verrouillées). Export version ${r.export.version} produit.`);
              }} />
          )}
          {fenetre?.genre === 'rouvrir' && (
            <FenetreConfirmation titre={`Rouvrir ${moisLong(mois)} — ${fenetre.site.site_nom}`} Icone={LockOpen} bouton="Rouvrir" variante="danger" motifRequis
              libelleMotif="Motif de la réouverture" aideMotif="Obligatoire et journalisé. La réouverture est limitée dans le temps ; une autre personne devra reclôturer."
              texte="Exceptionnel : à n'utiliser que si une régularisation sur le mois suivant ne suffit pas. Impossible si l'export a déjà été intégré en paie."
              onFermer={() => setFenetre(null)}
              onValider={async (motif) => { await ecrireRh('post', `/periodes/${fenetre.site.periode.id}/rouvrir`, { motif }); termine(`${fenetre.site.site_nom} : ${moisLong(mois)} rouvert.`); }} />
          )}
          {fenetre?.genre === 'reediter' && (
            <FenetreConfirmation titre={`Rééditer l'export — ${fenetre.site.site_nom}`} Icone={RotateCcw} bouton="Rééditer" motifRequis
              libelleMotif="Motif de la réédition" texte="Une nouvelle version est produite ; la précédente est marquée « remplacée »."
              onFermer={() => setFenetre(null)}
              onValider={async (motif) => { const r = await ecrireRh('post', `/exports/periode/${fenetre.site.periode.id}/reediter`, { motif }); termine(`Export réédité : version ${r.export.version}.`); }} />
          )}
          {fenetre?.genre === 'integrer' && (
            <FenetreConfirmation titre={`Marquer intégré en paie — version ${fenetre.exp.version}`} Icone={FileCheck2} bouton="Confirmer l'intégration"
              texte={`À confirmer une fois la version ${fenetre.exp.version} reprise dans la paie (empreinte ${String(fenetre.exp.empreinte).slice(0, 12)}…). Ensuite, la période ne pourra plus être rouverte : toute correction passera par une régularisation.`}
              onFermer={() => setFenetre(null)}
              onValider={async () => { await ecrireRh('patch', `/exports/${fenetre.exp.id}/integrer`); termine(`Version ${fenetre.exp.version} marquée intégrée en paie.`); }} />
          )}
        </AccesTemps>
      </div>
    </Layout>
  );
}
